import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Github } from '../components/AllSvgs';
import {motion} from 'framer-motion'

const Box = styled(motion.div)`
  width: 280px;
  min-width: 220px;
  max-width: 95vw;
  background-color: ${props => props.isToggled ? props.theme.body : props.theme.text};
  color: ${props => props.isToggled ? props.theme.text : props.theme.body};
  padding: 1.5rem 2rem;
  border-radius: 0 50px 0 50px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: 1px solid ${props => props.isToggled ? props.theme.text : props.theme.body};
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(0,0,0,0.2);
  flex-shrink: 0;
  height: 380px;
  cursor: grab;
  
  /* Prevent text selection during swiping */
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  overflow: hidden;

  /* Hover effects for desktop/devices with mouse */
  @media (hover: hover) {
    &:hover {
      background-color: ${props => props.theme.body};
      color: ${props => props.theme.text};
      border: 1px solid ${props => props.theme.text};
      transform: translateY(-10px);
    }
  }
  
  /* Remove conflicting active states for mobile to prevent flickering */
  &:active {
    cursor: grabbing;
  }
  
  @media (min-width: 1600px) {
    width: 320px;
    height: 420px;
    padding: 1.8rem 2.2rem;
  }
  
  @media (max-width: 1200px) {
    width: 260px;
    height: 360px;
    padding: 1.3rem 1.8rem;
  }
  
  @media (max-width: 768px) {
    width: 220px;
    min-width: 180px;
    height: 320px;
    padding: 1rem 1.5rem;
  }
  
  @media (max-width: 480px) {
    width: 60vw;
    min-width: 0;
    max-width: 65vw;
    height: 340px;
    padding: 0.8rem 0.7rem;
  }
`;

const Title = styled.h2`
  font-size: 1.5rem;
  margin-top: 0;
  margin-bottom: 0.5rem;
  word-break: break-word;
  overflow-wrap: anywhere;
  
  @media (min-width: 1600px) {
    font-size: 1.7rem;
  }
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const Description = styled.p`
  font-size: 1rem;
  font-family: 'Karla', sans-serif;
  font-weight: 400;
  margin: 0.5rem 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  flex-grow: 1;
  word-break: break-word;
  overflow-wrap: anywhere;
  
  @media (min-width: 1600px) {
    font-size: 1.1rem;
    -webkit-line-clamp: 5;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    -webkit-line-clamp: 3;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    -webkit-line-clamp: 5;
    margin: 0.3rem 0;
  }
`;

const Tags = styled.div`
  border-top: 2px solid ${props => props.isToggled ? props.theme.text : props.theme.body};
  padding-top: 0.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;

  /* Hover effects for desktop */
  @media (hover: hover) {
    ${Box}:hover & {
      border-top: 2px solid ${props => props.theme.text};
    }
  }
  
  @media (max-width: 768px) {
    padding-top: 0.3rem;
    margin-bottom: 0.5rem;
  }
`;

const Tag = styled.span`
  font-size: 0.8rem;
  
  @media (min-width: 1600px) {
    font-size: 0.9rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.6rem;
  }
`;

const Footer = styled.footer`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledLink = styled.a`
  background-color: ${props => props.isToggled ? props.theme.text : props.theme.body};
  color: ${props => props.isToggled ? props.theme.body : props.theme.text};
  text-decoration: none;
  padding: 0.5rem 1rem;
  border-radius: 0 0 0 20px;
  font-size: 1rem;
  display: inline-block;
  transition: all 0.3s ease;

  /* Hover effects for desktop */
  @media (hover: hover) {
    &:hover {
      background-color: ${props => props.theme.text};
      color: ${props => props.theme.body};
      transform: scale(1.05);
    }
  }
  
  /* Touch effects for mobile */
  @media (hover: none) {
    &:active {
      background-color: ${props => props.theme.text};
      color: ${props => props.theme.body};
      transform: scale(1.02);
      transition: all 0.1s ease;
    }
  }
  
  @media (min-width: 1600px) {
    font-size: 1.1rem;
    padding: 0.6rem 1.2rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.4rem 0.8rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 0.3rem 0.6rem;
  }
`;

const Git = styled.a`
  color: inherit;
  text-decoration: none;
  transition: transform 0.3s ease;
  
  /* Update SVG fill based on toggle state */
  &>*{
    fill: ${props => props.isToggled ? props.theme.text : props.theme.body};
    transition: fill 0.3s ease;
  }
  
  /* Hover effects for desktop */
  @media (hover: hover) {
    &:hover {
      transform: scale(1.2);
    }
    
    ${Box}:hover &{
      &>*{
        fill: ${props => props.theme.text}
      }
    }
  }
  
  @media (min-width: 1600px) {
    & > svg {
      width: 35px;
      height: 35px;
    }
  }
  
  @media (max-width: 768px) {
    & > svg {
      width: 25px;
      height: 25px;
    }
  }
  
  @media (max-width: 480px) {
    & > svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const Item = {
  hidden: {
    scale: 0
  },
  show: {
    scale: 1,
    transition: {
      type: 'spring',
      duration: 0.5
    }
  }
};

const Card = (props) => {
  const { id, name, description, tags, demo, github } = props.data;
  const [isToggled, setIsToggled] = useState(false);
  const cardRef = useRef(null);

  const handleCardClick = (e) => {
    // Only toggle on mobile devices (touch-only devices)
    if (window.matchMedia('(hover: none)').matches) {
      // Prevent toggle if clicking on links
      if (e.target.tagName === 'A' || e.target.closest('a')) {
        return;
      }
      // Toggle to dark mode and stay there
      if (!isToggled) {
        setIsToggled(true);
      }
    }
  };

  // Handle click outside to reset card to normal mode
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only handle on mobile devices
      if (window.matchMedia('(hover: none)').matches) {
        if (cardRef.current && !cardRef.current.contains(event.target)) {
          setIsToggled(false);
        }
      }
    };

    // Add event listener when card is toggled
    if (isToggled) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    // Cleanup event listeners
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isToggled]);

  return (
    <Box 
      key={id} 
      variants={Item} 
      data-card
      isToggled={isToggled}
      onClick={handleCardClick}
      ref={cardRef}
    >
      <Title>{name}</Title>
      <Description>{description}</Description>
      <Tags isToggled={isToggled}>
        {tags.map((t, id) => (
          <Tag key={id}>#{t}</Tag>
        ))}
      </Tags>
      <Footer>
        <StyledLink 
          href={demo} 
          target="_blank" 
          rel="noopener noreferrer"
          isToggled={isToggled}
        >
          Visit
        </StyledLink>
        {github && (
          <Git 
            href={github} 
            target="_blank" 
            rel="noopener noreferrer"
            isToggled={isToggled}
          >
            <Github width={30} height={30} />
          </Git>
        )}
      </Footer>
    </Box>
  );
};

export default Card;